import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { resetPasswordValidation, calculateStrength } from 'validation/auth.validation';
import { hidePassword, showPassword } from 'assets/newIcons';
import MobileContext from 'context/MobileContext';
import Button from 'components/shared/Button';
import styles from './style.module.css';
import history from 'store/history';
import { CircularProgress } from '@mui/material';
import authUtils from 'utils/auth';
import dxApi from 'services/dxApi';
import { isOnline, openSocketConnection, getSocket } from 'utils/socket';
import Spinner from 'components/shared/Spinner';

const initialValues = {
  password: '',
  confirmPassword: '',
};

const passwordRequirements = [
  'Your password must be a minimum of 6 characters long.',
  'We suggest it contains at least one capital letter, one number, and one special character (?!@#$%^--&*()_+`~).',
];

const MagicLinkPassword = ({
  validateMagicLink,
  isValidating,
  validatedData,
  resetValidatedData,
  setPasswordMagicLink,
  isSettingPassword,
  setPasswordData,
}) => {
  const isMobile = useContext(MobileContext);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(-1);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSetupLaterLoading, setIsSetupLaterLoading] = useState(false);
  const [isPasswordRedirecting, setIsPasswordRedirecting] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  useEffect(() => {
    if (token) {
      validateMagicLink(token);
    } else {
      history.push('/login');
    }

    // Cleanup function to reset validatedData when component unmounts
    return () => {
      resetValidatedData();
    };
  }, [token]);

  // Auto-redirect for non-OLS users
  useEffect(() => {
    if (validatedData?.success && !validatedData?.data?.isOls) {
      setIsRedirecting(true);
      redirectionFlow();
    }
  }, [validatedData]);

  useEffect(() => {
    if (setPasswordData && Object.keys(setPasswordData).length > 0) {
      setIsPasswordRedirecting(true);
      redirectionFlow();
    }
  }, [setPasswordData]);

  const togglePasswordVisibility = () => {
    setShowPasswordField(!showPasswordField);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPasswordField(!showConfirmPasswordField);
  };

  const handlePasswordChange = (e, handleChange) => {
    const { value } = e.target;
    handleChange(e);

    const displayStrength = calculateStrength(value);
    setPasswordStrength(displayStrength);
  };

  const handleSetupLaterClick = () => {
    setIsSetupLaterLoading(true);
    redirectionFlow();
  };

  const redirectionFlow = () => {
    if (validatedData?.success && validatedData?.data) {
      const loginData = validatedData.data;

      try {
        // Set authentication token
        authUtils.setToken(loginData.token, loginData.user.sfId);

        // Set token in API headers
        dxApi.setAuthTokenToHeader(loginData.token);

        // Connect socket
        const socket = getSocket();
        if (socket.disconnected) openSocketConnection();
        isOnline({ id: loginData.user.sfId });

        localStorage.removeItem('polkMemberPopupClosed');
        localStorage.removeItem('isPolkMember');
        localStorage.removeItem('polkPopupShown');
        localStorage.removeItem('tncApiChecked');
        if (loginData.user.isPolkMember) {
          localStorage.setItem('isPolkMember', 'true');
        }

        // Force a small delay to ensure token is set before redirect
        setTimeout(() => {
          if (loginData.user.isOnboardingDisplay) {
            window.location.href = '/onboarding';
          } else {
            window.location.href = '/';
          }
        }, 300);
      } catch (error) {
        console.error('Error during setup later process:', error);
        // Reset loading states in case of error
        setIsRedirecting(false);
        setIsSetupLaterLoading(false);
        setIsPasswordRedirecting(false);
      }
    } else {
      console.log('No valid data found in validatedData:');
      // Reset loading states if no valid data
      setIsRedirecting(false);
      setIsSetupLaterLoading(false);
      setIsPasswordRedirecting(false);
    }
  };

  const handleBackClick = () => {
    history.push('/login');
  };

  return (
    <div className={styles.page}>
      {isValidating || Object.keys(validatedData).length === 0 || isRedirecting ? (
        <Spinner />
      ) : Object.keys(validatedData).length > 0 && !validatedData.success ? (
        <div className={styles.errorContainer}>
          <p
            className={styles.errorMessage}
            dangerouslySetInnerHTML={{ __html: validatedData.message }}
          />
          {(validatedData?.data?.isInvalid ||
            validatedData?.data?.isUsed ||
            validatedData?.data?.isExpiry) && (
            <p className={`${styles.link}`} onClick={handleBackClick}>
              Back to Login
            </p>
          )}
        </div>
      ) : !validatedData?.data?.isOls ? (
        // This branch should never render due to the isRedirecting state,
        // but adding as a fallback
        <Spinner />
      ) : (
        <div className={isMobile ? '' : styles.container}>
          <Formik
            initialValues={initialValues}
            validationSchema={resetPasswordValidation}
            onSubmit={values => {
              const hubspotId = validatedData?.data?.user?.sfId;
              if (hubspotId) {
                setPasswordMagicLink({
                  password: values.password,
                  hubspotId,
                });
              }
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              handleBlur,
              isValid,
              dirty,
            }) => (
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.fieldContainer}>
                  <label className={styles.label}>Create Password</label>
                  <div className={styles.inputContainer}>
                    <input
                      type={showPasswordField ? 'text' : 'password'}
                      name="password"
                      value={values.password}
                      onChange={e => handlePasswordChange(e, handleChange)}
                      onBlur={handleBlur}
                      placeholder={isMobile ? 'Password' : ''}
                      disabled={isValidating}
                      className={`${styles.input} ${
                        touched.password && Boolean(errors.password) ? styles.invalid : ''
                      }`}
                    />
                    <div
                      className={styles.togglePassword}
                      onClick={togglePasswordVisibility}
                      role="button"
                      tabIndex={0}
                      style={{
                        backgroundImage: `url(${
                          showPasswordField ? hidePassword : showPassword
                        })`,
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: isMobile ? '26px 26px' : '20px 20px',
                      }}
                      aria-label="Toggle password visibility"
                    ></div>
                  </div>
                  <div className={styles.passwordStrength}>
                    <span className={styles.strengthText}>PASSWORD STRENGTH:</span>
                    {passwordStrength >= 0 && (
                      <span
                        className={`${styles.strengthIndicatorText} ${
                          passwordStrength === 0 || passwordStrength === 1
                            ? styles.weakText
                            : passwordStrength === 4
                            ? styles.strongText
                            : styles.mediumText
                        }`}
                      >
                        {passwordStrength === 0 || passwordStrength === 1
                          ? 'WEAK'
                          : passwordStrength === 4
                          ? 'STRONG'
                          : 'MEDIUM'}
                      </span>
                    )}
                    <div className={styles.strengthDots}>
                      <div
                        className={`${styles.dot} ${
                          passwordStrength === 0 || passwordStrength === 1
                            ? styles.dotWeak
                            : passwordStrength === 4
                            ? styles.dotStrong
                            : passwordStrength >= 1
                            ? styles.dotActive
                            : ''
                        }`}
                      ></div>
                      <div
                        className={`${styles.dot} ${
                          passwordStrength === 4
                            ? styles.dotStrong
                            : passwordStrength >= 2
                            ? styles.dotActive
                            : ''
                        }`}
                      ></div>
                      <div
                        className={`${styles.dot} ${
                          passwordStrength === 4
                            ? styles.dotStrong
                            : passwordStrength >= 3
                            ? styles.dotActive
                            : ''
                        }`}
                      ></div>
                      <div
                        className={`${styles.dot} ${
                          passwordStrength === 4 ? styles.dotStrong : ''
                        }`}
                      ></div>
                    </div>
                  </div>
                  {touched.password && errors.password && (
                    <div className={styles.error}>{errors.password}</div>
                  )}
                </div>

                <div className={styles.fieldContainer} style={{ marginTop: '20px' }}>
                  <label className={styles.label}>Confirm Password</label>
                  <div className={styles.inputContainer}>
                    <input
                      type={showConfirmPasswordField ? 'text' : 'password'}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      placeholder={isMobile ? 'Password' : ''}
                      onBlur={handleBlur}
                      disabled={isValidating}
                      className={`${styles.input} ${
                        touched.confirmPassword && Boolean(errors.confirmPassword)
                          ? styles.invalid
                          : ''
                      }`}
                    />
                    <div
                      className={styles.togglePassword}
                      onClick={toggleConfirmPasswordVisibility}
                      role="button"
                      tabIndex={0}
                      style={{
                        backgroundImage: `url(${
                          showConfirmPasswordField ? hidePassword : showPassword
                        })`,
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: isMobile ? '26px 26px' : '20px 20px',
                      }}
                      aria-label="Toggle password visibility"
                    ></div>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className={styles.error}>{errors.confirmPassword}</div>
                  )}
                </div>

                <div className={styles.passwordInfo}>
                  {passwordRequirements.map((requirement, index) => (
                    <div key={index} className={styles.passwordInfoItem}>
                      {requirement}
                    </div>
                  ))}
                </div>

                <Button
                  type="submit"
                  className={styles.customButton}
                  disabled={
                    isValidating ||
                    !(isValid && dirty) ||
                    isSettingPassword ||
                    isPasswordRedirecting ||
                    isSetupLaterLoading
                  }
                >
                  {isSettingPassword || isPasswordRedirecting ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                      style={{ color: '#fe5000' }}
                    />
                  ) : (
                    'Next'
                  )}
                </Button>

                <div className={styles.setupLaterContainer}>
                  {isSetupLaterLoading ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ color: '#fe5000' }}
                    />
                  ) : (
                    <p
                      className={styles.setupLater}
                      disabled={isSettingPassword || isPasswordRedirecting}
                      onClick={handleSetupLaterClick}
                    >
                      Set Up Later in Profile
                    </p>
                  )}
                </div>
              </form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

MagicLinkPassword.propTypes = {
  validateMagicLink: PropTypes.func.isRequired,
  resetValidatedData: PropTypes.func.isRequired,
  setPasswordMagicLink: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
  isSettingPassword: PropTypes.bool,
  validatedData: PropTypes.object,
  setPasswordData: PropTypes.object,
};

MagicLinkPassword.defaultProps = {
  isValidating: false,
  isSettingPassword: false,
  validatedData: {},
  setPasswordData: {},
};

export default MagicLinkPassword;
